import { offset, shift, useFloating } from '@floating-ui/react';
import { ChevronLeft, ChevronRight } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import React, { PropsWithoutRef, forwardRef, useImperativeHandle } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Control, FieldValues, Path, RegisterOptions, useController } from 'react-hook-form';

import { ButtonGroup, Collapsible, Decorator, DecoratorProps, IconButton } from '@myhealth-raccoon/components';

import { CheckboxItem } from './CheckboxItem';
import { InlineModal } from 'src/components/InlineModal.tsx';
import { useBreakpoints } from 'src/hooks/useBreakpoints';

type Props<T, TForm extends FieldValues> = {
    items: T[];
    control: Control<TForm>;
    name: Path<TForm>;
    rules?: RegisterOptions<TForm, Path<TForm>>;
    getItemValue(item: T): string | number;
    render(item: T): React.ReactNode;
    stringValue?: string;
    display?: 'pagination' | 'all' | 'show-more';
    size?: 'sm' | 'lg';
    value?: string;
    closeOnChange?: boolean;
    subtitle?: React.ReactNode;
    className?: string;
} & DecoratorProps;

function getPageSize(
    display: 'show-more' | 'pagination',
    size: 'sm' | 'lg',
    breakpoints: 'xs' | 'lg' | 'sm' | 'md' | 'xl' | '2xl'
): number {
    if (display === 'show-more') {
        const fullRow = getPageSize('pagination', size, breakpoints);
        return ~~(9 / fullRow) * fullRow;
    }
    switch (size) {
        case 'sm':
        default:
            switch (breakpoints) {
                case 'xs':
                    return 2;
                case 'sm':
                    return 3;
                case 'md':
                    return 4;
                case 'lg':
                    return 6;
                default:
                    return 6;
            }
        case 'lg':
            switch (breakpoints) {
                case 'xs':
                    return 1;
                case 'sm':
                    return 2;
                case 'md':
                    return 3;
                case 'lg':
                    return 4;
                default:
                    return 5;
            }
    }
}

// 1. Create a generic wrapper utility
function fixedForwardRef<T, P = unknown>(
    render: (props: PropsWithoutRef<P>, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
    return forwardRef(render) as never;
}

export const RadioList = fixedForwardRef(InnerRadioList);

export interface RadioListRef {
    openSelection(): void;
    collapse(): void;
    expand(): void;
}

function InnerRadioList<T, TForm extends FieldValues>(
    {
        fieldError,
        label,
        hint,
        control,
        name,
        rules,
        items,
        id,
        value: fieldValue,
        stringValue,
        subtitle: subtitleProp,
        getItemValue,
        render,
        display = 'all',
        size = 'sm',
        closeOnChange,
        className,
    }: Props<T, TForm>,
    ref: React.Ref<RadioListRef>
) {
    const [page, setPage] = useState(1);
    const [showMore, setShowMore] = useState(false);
    const [open, setIsOpen] = useState(true);
    const breakpoints = useBreakpoints();
    const pageSize = useMemo(() => {
        if (display === 'all') {
            return Infinity;
        }
        return getPageSize(display, size, breakpoints);
    }, [breakpoints, display, size]);

    const memoedRules: RegisterOptions<TForm, Path<TForm>> | undefined = useMemo(() => {
        if (!closeOnChange) {
            return rules;
        }
        return {
            ...(rules ?? {}),
            onChange(e) {
                rules?.onChange?.(e);
                setIsOpen(false);
            },
        };
    }, [closeOnChange, rules]);

    const { field } = useController({
        name,
        control,
        rules: memoedRules,
    });

    useEffect(() => {
        if (!fieldValue) {
            setIsOpen(true);
        }
    }, [items, fieldValue]);

    const thisPage = useMemo(() => {
        if (display === 'pagination') {
            return items.slice((page - 1) * pageSize, page * pageSize);
        } else if (display === 'show-more' && items.length > pageSize) {
            return items.slice(0, pageSize - 1);
        }
        return items;
    }, [display, items, page, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [items, pageSize]);

    const { refs } = useFloating({
        open: showMore,
        placement: 'top',
        strategy: 'fixed',
        transform: false,
        middleware: [
            offset(-100),
            shift({
                crossAxis: true,
            }),
        ],
    });

    useEffect(() => {
        if (showMore) {
            document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [showMore]);

    const startIndex = (page - 1) * pageSize;

    const subtitle = (
        <strong className="font-semibold text-gray-700">
            {fieldError?.message ? (
                <strong className="text-error">{fieldError?.message}</strong>
            ) : (
                subtitleProp || stringValue || '-'
            )}
        </strong>
    );

    useImperativeHandle(
        ref,
        () => ({
            openSelection: () => setShowMore(true),
            expand: () => setIsOpen(true),
            collapse: () => setIsOpen(false),
        }),
        []
    );

    return (
        <Collapsible
            className={className}
            open={open}
            setOpen={setIsOpen}
            title={
                <div className="flex flex-row items-center gap-4">
                    <span className="text-lg font-normal text-gray-600">
                        {label}: <span className="hidden sm:inline">{subtitle}</span>
                    </span>
                    {display === 'pagination' && !!thisPage.length && (
                        <div className="ml-auto flex flex-row items-center">
                            Showing {startIndex + 1}-{startIndex + thisPage.length} of {items.length}
                            <ButtonGroup className="ml-2.5 shadow-sm">
                                <IconButton
                                    icon={ChevronLeft}
                                    disabled={page === 1}
                                    onClick={() => setPage((p) => p - 1)}
                                    type="button"
                                />
                                <IconButton
                                    icon={ChevronRight}
                                    disabled={page * pageSize >= items.length}
                                    onClick={() => setPage((p) => p + 1)}
                                    type="button"
                                />
                            </ButtonGroup>
                        </div>
                    )}
                </div>
            }
            subtitle={<div className="sm:hidden">{subtitle}</div>}
        >
            <Decorator fieldset hint={hint}>
                <div
                    className={classNames('grid gap-4', {
                        'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6': size === 'sm',
                        'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5': size === 'lg',
                        hidden: !items.length,
                    })}
                >
                    {thisPage.map((item) => {
                        const value = getItemValue(item);
                        return (
                            <CheckboxItem
                                type="radio"
                                className="animate-fade-in"
                                {...field}
                                checked={fieldValue ? fieldValue === value.toString() : undefined}
                                value={value.toString()}
                                key={value}
                                id={`${id}_${value}`}
                                tight={size === 'sm'}
                            >
                                {render(item)}
                            </CheckboxItem>
                        );
                    })}
                    {display === 'show-more' && items.length > pageSize && (
                        <button
                            type="button"
                            ref={refs.setPositionReference}
                            onClick={() => setShowMore(true)}
                            className="text-primary-700 text-sm font-medium hover:underline"
                        >
                            Show all {items.length} options
                        </button>
                    )}
                </div>
                {display === 'show-more' && (
                    <InlineModal
                        isOpen={showMore && items.length > 0}
                        onClose={() => setShowMore(false)}
                        size="lg"
                        title="Select an appointment type"
                    >
                        <div
                            className={classNames('p-6', 'my-0! mt-4 grid gap-4', {
                                'grid-cols-2 sm:grid-cols-3 md:grid-cols-4': size === 'sm',
                                'grid-cols-1 md:grid-cols-2 xl:grid-cols-3': size === 'lg',
                            })}
                        >
                            {items.map((item) => {
                                const value = getItemValue(item);
                                return (
                                    <CheckboxItem
                                        type="radio"
                                        className="animate-fade-in"
                                        {...field}
                                        value={value.toString()}
                                        checked={fieldValue ? fieldValue === value.toString() : undefined}
                                        key={value}
                                        id={`${id}_modal_${value}`}
                                        onClick={() => setShowMore(false)}
                                        tight={size === 'sm'}
                                    >
                                        {render(item)}
                                    </CheckboxItem>
                                );
                            })}
                        </div>
                    </InlineModal>
                )}
            </Decorator>
        </Collapsible>
    );
}
